<script setup lang="ts">
import getAllQuaterlyReportsQuery from '@/graphql/getAllQuaterlyReports.gql'

const localePath = useLocalePath()
const { t, locale } = useI18n()

defineI18nRoute({
  paths: {
    'en-CA': '/investor-relations/financial-documents/quarterly-reports',
    'fr-CA': '/relations-avec-investisseurs/documents-financiers/rapports-trimestriels'
  }
})

useHead({
  title: t('navigation.quarterlyReports.metatitle'),
  meta: [{ name: 'description', content: t('navigation.quarterlyReports.metadescription') }]
})

const { execute: getQuaterlyReports } = useApolloFetch(getAllQuaterlyReportsQuery, 'quaterlyReports')
const quaterlyReports = await getQuaterlyReports({ locale: locale.value })
const yearlyReportsMap = new Map()
for (const report of quaterlyReports) {
  if (!yearlyReportsMap.has(report.Year)) {
    yearlyReportsMap.set(report.Year, [])
  }
  yearlyReportsMap.get(report.Year).push(report)
}

function getReportsLabel(report: { EnglishDocumentOnly: boolean }, path: string) {
  return `${t(`quaterlyReports.${path}`)}${report.EnglishDocumentOnly && locale.value === 'fr-CA' ? ' (disponible en anglais seulement)' : ''}`
}

function getSizeInMB(size: number) {
  return (size / 1024).toFixed(2)
}

</script>

<template>
  <div class="wrapContent">
    <div class="entetePage">
      <div class="wrapper">
        <div class="filAriane">
          <span class="">
            <NuxtLink :to="localePath('index')">{{ $t('navigation.home.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <NuxtLink :to="localePath('investorRelations-investorRelations')">{{ $t('navigation.investorRelations.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <NuxtLink :to="localePath('investorRelations-shareholderInformation')">{{ $t('navigation.financialDocuments.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.quarterlyReports.name') }}</span>
          </span>
        </div>
        <div class="title">
          <h1><span class="">{{ $t('navigation.quarterlyReports.name') }}</span></h1>
        </div>
      </div>
    </div>
    <div class="contentPage">
      <div class="wrapper clearfix">
        <MenuInvestorRelations />
        <div class="contenu right">
          <div class="GpcLibraryExplorer">
            <ul class="GpcLibraryExplorerWrapQueryList">
              <li v-for="[year, reports] in yearlyReportsMap"
                  :key="year"
                  class="GpcLibraryExplorerWrapQueryListItem">
                <div class="LibraryExplorerCategoryQueryZone">
                  <div class="LibraryExplorerCategoryContentZone">
                    <div class="LibraryExplorerCategoryTitleZone">
                      <span class="LibraryExplorerCategoryTitle">{{ year }}</span>
                    </div>
                  </div>
                </div>
                <ul class="GpcLibraryExplorerWrapQueryList">
                  <li v-for="report in reports"
                      :key="report.Quarter"
                      class="GpcLibraryExplorerWrapQueryListItem">
                    <div class="LibraryExplorerCategoryQueryZone">
                      <div class="LibraryExplorerCategoryContentZone">
                        <div class="LibraryExplorerCategoryTitleZone">
                          <span class="LibraryExplorerCategoryTitle">{{ $t(`quaterlyReports.${report.Quarter}`, { year: report.Year }) }}</span>
                        </div>
                      </div>
                    </div>

                    <ul class="GpcLibraryExplorerWrapQueryList">
                        <!-- Rapport de gestion -->
                        <li v-if="report.KpiMda"
                          class="GpcLibraryExplorerWrapQueryListItem">
                        <div class="LibraryExplorer">
                          <div class="LibraryExplorerResultContentZone">
                            <div class="LibraryExplorerResultTitleZone">
                              <a  v-if="year > '2022'"
                                  :href="report.KpiMda.url"
                                  target="_blank">{{ getReportsLabel(report, "kpiMdaLabel") }}</a>
                              <a  v-else
                                  :href="report.KpiMda.url"
                                  target="_blank">{{ getReportsLabel(report, "kplpMdaLabel") }}</a>
                            </div>
                            <div class="separateur">
                              &nbsp;-&nbsp;
                            </div>
                            <div class="LibraryExplorerResultFileSizeZone">
                              <span>{{ getSizeInMB(report.KpiMda.size) }} Mb</span>
                            </div>
                          </div>
                        </div>
                      </li>

                      <!-- États KP Tissue -->
                      <li v-if="report.FinancialStatement"
                          class="GpcLibraryExplorerWrapQueryListItem">
                        <div class="LibraryExplorer">
                          <div class="LibraryExplorerResultContentZone">
                            <div class="LibraryExplorerResultTitleZone">
                              <a :href="report.FinancialStatement.url"
                                 target="_blank">{{ getReportsLabel(report, "kptStatementLabel") }}</a>
                            </div>
                            <div class="separateur">
                              &nbsp;-&nbsp;
                            </div>
                            <div class="LibraryExplorerResultFileSizeZone">
                              <span>{{ getSizeInMB(report.FinancialStatement.size) }} Mb</span>
                            </div>
                          </div>
                        </div>
                      </li>

                      <!-- États KPI -->
                      <li v-if="report.KpFinancialStatement"
                          class="GpcLibraryExplorerWrapQueryListItem">
                        <div class="LibraryExplorer">
                          <div class="LibraryExplorerResultContentZone">
                            <div class="LibraryExplorerResultTitleZone">
                              <a v-if="year > '2022'" :href="report.KpFinancialStatement.url"
                                 target="_blank">{{ getReportsLabel(report, "kpiStatementLabel") }}</a>
                              <a v-else :href="report.KpFinancialStatement.url"
                                 target="_blank">{{ getReportsLabel(report, "kplpStatementLabel") }}</a>
                            </div>
                            <div class="separateur">
                              &nbsp;-&nbsp;
                            </div>
                            <div class="LibraryExplorerResultFileSizeZone">
                              <span>{{ getSizeInMB(report.KpFinancialStatement.size) }} Mb</span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
